import clsx from "clsx"
import BodyText from "components/Typography/BodyText"
import Caption from "components/Typography/Caption"
import { memo, useCallback, useMemo, useState } from "react"
import styled from "styled-components"

import { stringNormalization } from "utils/stringUtils/stringNormalization"
import { GroupBase, OptionsOrGroups } from "react-select"
import { AsyncPaginate } from "react-select-async-paginate"
import IconError from "../icons/IconError"

const InputSection = styled.div`
  position: relative;
  width: 100%;
  max-width: 340px;

  &.fullWidth {
    max-width: 100%;
  }

  .control-styles {
    font-size: 14px;
    background-color: transparent;
    &:hover {
      border-color: ${(props) => props.theme.palette.primary_one};
    }
    &:active {
    }
    &:focus {
    }
    border-radius: 8px;
    border-color: ${(props) => props.theme.palette.primary_one};
    svg {
      color: ${(props) => props.theme.palette.primary_one};
    }
    span {
      display: none;
    }
  }

  .single-value-styles {
    color: ${(props) => props.theme.palette.primary_one};
  }

  .menu-styles {
    color: ${(props) => props.theme.palette.primary_one};
    font-size: 14px;
  }

  &.disabled {
    .control-styles {
      border-color: ${(props) => props.theme.palette.disabled};
      background-color: ${(props) => props.theme.palette.white};
      svg {
        color: ${(props) => props.theme.palette.disabled};
      }
    }
    .single-value-styles {
      color: ${(props) => props.theme.palette.disabled};
    }
  }
`

const LabelForm = styled.label`
  position: absolute;
  pointer-events: none;
  left: 15px;
  top: 14px;
  transition: 0.2s ease all;
  z-index: 120;
  color: ${(props) => props.theme.palette.primary_one};

  &.shrink {
    top: -8px;
    padding: 0 4px 0 4px;
    background-color: ${(props) => props.theme.palette.white};
  }
`

const ErrorText = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${(props) => props.theme.palette.error};
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 4px;
  height: 18px;

  &.unvisible {
    width: 0px;
    overflow: hidden;
  }
`

export interface Option {
  value: string
  label: string
}

interface AsyncSelectProps {
  id: string
  disabled?: boolean
  name?: string
  value?: string
  label: string
  onChange: (options: Option | null) => void
  getData?: (
    search: string,
    skip: number
  ) => Promise<{
    hasMore: boolean
    results: {
      value: string
      label: string
    }[]
  } | null>
  options?: Option[] | undefined
  dependencies?: any[]
  placeholder?: string
  showErrorSection?: boolean
  error?: string | boolean
  freeSolo?: boolean
  shrink?: boolean
  className?: string
}

const SelectV2: React.FC<AsyncSelectProps> = (props) => {
  const {
    id,
    disabled = false,
    value,
    label,
    onChange,
    getData,
    options,
    dependencies,
    placeholder = "",
    showErrorSection = false,
    error = "",
    freeSolo,
    shrink = false,
    className
  } = props

  const errorVisible = error ? "" : "unvisible"

  const isAsync = !!getData

  const [isFocused, setIsFocused] = useState(false)

  const [localOptions, setLocalOptions] = useState<Option[] | undefined>(options)

  const onFilterOption = useCallback((option: { value: string; label: string }, rawInput: string) => {
    const input = stringNormalization(rawInput.toLowerCase())
    const optionLabel = stringNormalization(option.label).toLowerCase()

    return optionLabel.includes(input)
  }, [])

  const handleFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
  }, [])

  const cacheKeys = useMemo(() => {
    if (!dependencies) return []

    if (!options) return [...(dependencies || []), options]

    return dependencies
  }, [dependencies, options])

  const loadOptions = async (search: string, prevOptions: OptionsOrGroups<Option, GroupBase<Option>>) => {
    let filteredOptions: Option[] | undefined

    if (getData) {
      const data = await getData(search, (prevOptions || []).length)

      if (!data) {
        return {
          options: [],
          hasMore: false
        }
      }

      setLocalOptions([...(localOptions || []), ...data.results])

      if (freeSolo) {
        const exists = data.results.find((option) => stringNormalization(option.value) === stringNormalization(search))

        if (search && !exists) {
          data.results.unshift({
            label: `${search} (Novo)`,
            value: search
          })
        }
      }

      return {
        options: data.results,
        hasMore: data.hasMore
      }
    }

    if (options) {
      const searchLower = search.toLowerCase()

      filteredOptions = searchLower
        ? options?.filter(({ label }) => stringNormalization(label).includes(stringNormalization(searchLower)))
        : options

      if (freeSolo) {
        if (search) {
          filteredOptions.push({
            label: `${search} (Novo)`,
            value: search
          })
        }

        if (localValue && !filteredOptions.find((option) => option.value === localValue.value)) {
          filteredOptions.push(localValue)
        }
      }
    }

    if (!filteredOptions) {
      return {
        options: [],
        hasMore: false
      }
    }

    const hasMore = filteredOptions.length > prevOptions.length + 20
    const slicedOptions = filteredOptions?.slice(prevOptions.length, prevOptions.length + 20)

    setLocalOptions([...(localOptions || []), ...slicedOptions])

    return {
      options: slicedOptions,
      hasMore
    }
  }

  const localValue = useMemo(() => {
    if (!value) return null

    let option = options?.find((option) => option.value === value)

    if (!option && localOptions) {
      option = localOptions.find((option) => option.value === value)
    }

    if (!option || freeSolo) {
      return { value, label: value }
    }

    return option
  }, [value, options, localOptions, freeSolo])

  return (
    <InputSection className={clsx(className, { disabled })}>
      <LabelForm htmlFor={id} className={clsx({ shrink: !!value || isFocused || shrink })}>
        <BodyText size="small" color={disabled ? "gray-light" : "primary-dark"}>
          {label}
        </BodyText>
      </LabelForm>
      <AsyncPaginate
        inputId={id}
        isDisabled={disabled}
        className="input-select-area"
        filterOption={onFilterOption}
        noOptionsMessage={() => "Nenhum item encontrado"}
        loadingMessage={() => "Carregando..."}
        value={localValue || null}
        isLoading={isAsync ? undefined : false}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={onChange}
        loadOptions={loadOptions}
        placeholder={placeholder}
        cacheUniqs={cacheKeys}
        debounceTimeout={500}
        classNames={{
          control: () => "control-styles",
          singleValue: () => "single-value-styles",
          menu: () => "menu-styles",
          valueContainer: () => "value-container-styles"
        }}
        menuPortalTarget={document.body}
        options={options}
        styles={{
          control(base) {
            return {
              ...base,
              svg: {
                transform: isFocused ? "rotate(180deg)" : "rotate(0deg)"
              }
            }
          },
          valueContainer: (base) => {
            return {
              ...base,
              padding: "8px 12px"
            }
          },
          menuPortal: (base) => {
            return {
              ...base,
              zIndex: 1001,
              fontSize: "14px",
              color: "#002C98"
            }
          },
          menuList: (base) => {
            return {
              ...base,
              maxHeight: "228px"
            }
          }
        }}
      />
      {showErrorSection && (
        <ErrorText id="input-error" className={clsx(errorVisible)}>
          <IconError />
          <Caption size="large" color="error">
            {error}
          </Caption>
        </ErrorText>
      )}
    </InputSection>
  )
}

export default memo(SelectV2)
